import {
  createSlice,
  createAsyncThunk,
  createEntityAdapter,
  createAction,
} from '@reduxjs/toolkit';
import { getTemperatureHumidityPressure } from 'scenes/Renolog/services/getTemperatureHumidityPressure.service';
import { getMouldRisk } from 'scenes/Renolog/services/getMouldRisk.service';
import { getStrength } from 'scenes/Renolog/services/getStrength.service';
import { getRenologDevices } from 'scenes/Renolog/services/getRenologDevices.service';
import { getCompanyDevices } from 'scenes/Renolog/services/getCompanyDevices.service';
import { getSensorOrders } from 'scenes/Renolog/services/getSensorOrders.service';
import { cancelSensorOrder as cancelSensorOrderService } from 'scenes/Renolog/services/cancelSensorOrder.service';

const deviceAdapter = createEntityAdapter({
  selectId: (entry) => entry.assignmentId,
});

const initialProjectState = () => ({
  ...deviceAdapter.getInitialState(),
  sensorReadings: {},
  sensorOrders: [],
  companyDevices: [],
  loading: {
    devices: false,
    sensorData: {},
    temperatureHumidity: {},
    mouldRisk: {},
    strength: {},
    sensorOrders: false,
    companyDevices: false,
  },
  loadingStarted: {
    devices: false,
    sensorData: {},
    strength: {},
    sensorOrders: false,
    companyDevices: false,
  },
  error: {
    devices: undefined,
    sensorData: {},
    sensorOrders: undefined,
    companyDevices: undefined,
  },
});

const ensureProject = (state, projectId) => {
  if (!state.byProject[projectId]) {
    state.byProject[projectId] = initialProjectState();
  }
};

export const sensorDataCleared = createAction(
  'renologSensorData/sensorDataCleared',
  ({ projectId, assignmentId }) => ({ payload: { projectId, assignmentId } })
);

export const sensorDataBatchReceived = createAction(
  'renologSensorData/sensorDataBatchReceived',
  ({
    projectId,
    assignmentId,
    temperatureHumidityBatch,
    mouldRiskBatch,
    strengthBatch,
  }) => ({
    payload: {
      projectId,
      assignmentId,
      temperatureHumidityBatch,
      mouldRiskBatch,
      strengthBatch,
    },
  })
);

export const temperatureHumidityLoadingChanged = createAction(
  'renologSensorData/temperatureHumidityLoadingChanged',
  ({ projectId, assignmentId, loading }) => ({
    payload: { projectId, assignmentId, loading },
  })
);

export const mouldRiskLoadingChanged = createAction(
  'renologSensorData/mouldRiskLoadingChanged',
  ({ projectId, assignmentId, loading }) => ({
    payload: { projectId, assignmentId, loading },
  })
);

export const strengthLoadingChanged = createAction(
  'renologSensorData/strengthLoadingChanged',
  ({ projectId, assignmentId, loading }) => ({
    payload: { projectId, assignmentId, loading },
  })
);

const fetchAllPages = async (fetchFn, onBatch) => {
  let lastKey;
  do {
    const result = await fetchFn(lastKey);
    onBatch(result.items);
    lastKey = result.lastEvaluatedKey || undefined;
  } while (lastKey);
};

export const fetchSensorData = createAsyncThunk(
  'renolog/fetchSensorData',
  async (
    { projectId, assignmentId },
    { rejectWithValue, dispatch, getState }
  ) => {
    try {
      const model = selectDeviceByAssignmentId(
        getState(),
        projectId,
        assignmentId
      )?.model;

      dispatch(sensorDataCleared({ projectId, assignmentId }));
      dispatch(
        temperatureHumidityLoadingChanged({
          projectId,
          assignmentId,
          loading: true,
        })
      );

      const fetches = [
        fetchAllPages(
          (lastKey) =>
            getTemperatureHumidityPressure(projectId, assignmentId, lastKey),
          (items) =>
            dispatch(
              sensorDataBatchReceived({
                projectId,
                assignmentId,
                temperatureHumidityBatch: items,
              })
            )
        ).finally(() =>
          dispatch(
            temperatureHumidityLoadingChanged({
              projectId,
              assignmentId,
              loading: false,
            })
          )
        ),
      ];

      if (model === 'BOX') {
        dispatch(
          mouldRiskLoadingChanged({ projectId, assignmentId, loading: true })
        );
        fetches.push(
          fetchAllPages(
            (lastKey) => getMouldRisk(projectId, assignmentId, lastKey),
            (items) =>
              dispatch(
                sensorDataBatchReceived({
                  projectId,
                  assignmentId,
                  mouldRiskBatch: items,
                })
              )
          ).finally(() =>
            dispatch(
              mouldRiskLoadingChanged({
                projectId,
                assignmentId,
                loading: false,
              })
            )
          )
        );
      }

      if (model === 'CURE') {
        dispatch(
          strengthLoadingChanged({ projectId, assignmentId, loading: true })
        );
        fetches.push(
          fetchAllPages(
            (lastKey) => getStrength(projectId, assignmentId, lastKey),
            (items) =>
              dispatch(
                sensorDataBatchReceived({
                  projectId,
                  assignmentId,
                  strengthBatch: items,
                })
              )
          ).finally(() =>
            dispatch(
              strengthLoadingChanged({
                projectId,
                assignmentId,
                loading: false,
              })
            )
          )
        );
      }

      await Promise.all(fetches);

      return { projectId, assignmentId };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch sensor data');
    }
  }
);

export const fetchStrengthData = createAsyncThunk(
  'renolog/fetchStrengthData',
  async ({ projectId, assignmentId }, { rejectWithValue, dispatch }) => {
    try {
      dispatch(
        strengthLoadingChanged({ projectId, assignmentId, loading: true })
      );
      await fetchAllPages(
        (lastKey) => getStrength(projectId, assignmentId, lastKey),
        (items) =>
          dispatch(
            sensorDataBatchReceived({
              projectId,
              assignmentId,
              strengthBatch: items,
            })
          )
      ).finally(() =>
        dispatch(
          strengthLoadingChanged({ projectId, assignmentId, loading: false })
        )
      );
      return { projectId, assignmentId };
    } catch (error) {
      dispatch(
        strengthLoadingChanged({ projectId, assignmentId, loading: false })
      );
      return rejectWithValue(error.message || 'Failed to fetch strength data');
    }
  }
);

export const fetchSensorOrders = createAsyncThunk(
  'renolog/fetchSensorOrders',
  async ({ projectId }, { rejectWithValue }) => {
    try {
      const orders = await getSensorOrders(projectId);
      return { orders, projectId };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch sensor orders');
    }
  }
);

export const cancelSensorOrder = createAsyncThunk(
  'renolog/cancelSensorOrder',
  async ({ projectId, orderId }, { rejectWithValue }) => {
    try {
      await cancelSensorOrderService(projectId, orderId);
      return { projectId, orderId };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to cancel sensor order');
    }
  }
);

export const fetchDevices = createAsyncThunk(
  'renolog/fetchDevices',
  async ({ projectId, doSync }, { rejectWithValue }) => {
    try {
      const devices = await getRenologDevices(projectId, doSync);
      return { devices, projectId, doSync };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch devices');
    }
  }
);

export const fetchCompanyDevices = createAsyncThunk(
  'renolog/fetchCompanyDevices',
  async ({ projectId }, { rejectWithValue }) => {
    try {
      const devices = await getCompanyDevices(projectId);
      return { devices, projectId };
    } catch (error) {
      return rejectWithValue(
        error.message || 'Failed to fetch company devices'
      );
    }
  }
);

const initialState = {
  byProject: {},
};

const sensorDataSlice = createSlice({
  name: 'renologSensorData',
  initialState,
  reducers: {
    sensorOrderAdded(state, action) {
      const { projectId, order } = action.payload;
      ensureProject(state, projectId);
      state.byProject[projectId].sensorOrders.push(order);
    },
    sensorOrderUpdated(state, action) {
      const { projectId, order } = action.payload;
      if (!state.byProject[projectId]) return;
      const index = state.byProject[projectId].sensorOrders.findIndex(
        (o) => o.id === order.id
      );
      if (index !== -1) {
        state.byProject[projectId].sensorOrders[index] = order;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(sensorDataCleared, (state, action) => {
        const { projectId, assignmentId } = action.payload;
        ensureProject(state, projectId);
        state.byProject[projectId].sensorReadings[assignmentId] = {
          temperatureHumidity: [],
          mouldRisk: [],
          strength: [],
        };
      })
      .addCase(temperatureHumidityLoadingChanged, (state, action) => {
        const { projectId, assignmentId, loading } = action.payload;
        ensureProject(state, projectId);
        state.byProject[projectId].loading.temperatureHumidity[assignmentId] =
          loading;
      })
      .addCase(mouldRiskLoadingChanged, (state, action) => {
        const { projectId, assignmentId, loading } = action.payload;
        ensureProject(state, projectId);
        state.byProject[projectId].loading.mouldRisk[assignmentId] = loading;
      })
      .addCase(strengthLoadingChanged, (state, action) => {
        const { projectId, assignmentId, loading } = action.payload;
        ensureProject(state, projectId);
        state.byProject[projectId].loading.strength[assignmentId] = loading;
      })
      .addCase(sensorDataBatchReceived, (state, action) => {
        const {
          projectId,
          assignmentId,
          temperatureHumidityBatch,
          mouldRiskBatch,
          strengthBatch,
        } = action.payload;
        const project = state.byProject[projectId];
        if (!project) return;
        const readings = project.sensorReadings[assignmentId];
        if (!readings) return;
        if (temperatureHumidityBatch) {
          readings.temperatureHumidity.push(...temperatureHumidityBatch);
        }
        if (mouldRiskBatch) {
          readings.mouldRisk.push(...mouldRiskBatch);
        }
        if (strengthBatch) {
          readings.strength.push(...strengthBatch);
        }
      })
      .addCase(fetchDevices.pending, (state, action) => {
        const { projectId } = action.meta.arg;
        ensureProject(state, projectId);
        state.byProject[projectId].loading.devices = true;
        state.byProject[projectId].loadingStarted.devices = true;
        state.byProject[projectId].error.devices = undefined;
      })
      .addCase(fetchDevices.fulfilled, (state, action) => {
        const { devices, projectId } = action.payload;
        ensureProject(state, projectId);
        deviceAdapter.setAll(state.byProject[projectId], devices);
        state.byProject[projectId].loading.devices = false;
        state.byProject[projectId].error.devices = undefined;
      })
      .addCase(fetchDevices.rejected, (state, action) => {
        const { projectId } = action.meta.arg;
        if (!state.byProject[projectId]) return;
        state.byProject[projectId].loading.devices = false;
        state.byProject[projectId].error.devices =
          action.payload || 'Unknown error';
      })
      .addCase(fetchSensorOrders.pending, (state, action) => {
        const { projectId } = action.meta.arg;
        ensureProject(state, projectId);
        state.byProject[projectId].loading.sensorOrders = true;
        state.byProject[projectId].loadingStarted.sensorOrders = true;
        state.byProject[projectId].error.sensorOrders = undefined;
      })
      .addCase(fetchSensorOrders.fulfilled, (state, action) => {
        const { orders, projectId } = action.payload;
        ensureProject(state, projectId);
        state.byProject[projectId].sensorOrders = orders;
        state.byProject[projectId].loading.sensorOrders = false;
        state.byProject[projectId].error.sensorOrders = undefined;
      })
      .addCase(fetchSensorOrders.rejected, (state, action) => {
        const { projectId } = action.meta.arg;
        if (!state.byProject[projectId]) return;
        state.byProject[projectId].loading.sensorOrders = false;
        state.byProject[projectId].error.sensorOrders =
          action.payload || 'Unknown error';
      })
      .addCase(fetchSensorData.pending, (state, action) => {
        const { projectId, assignmentId } = action.meta.arg;
        ensureProject(state, projectId);
        state.byProject[projectId].loading.sensorData[assignmentId] = true;
        state.byProject[projectId].loadingStarted.sensorData[assignmentId] =
          true;
        state.byProject[projectId].error.sensorData[assignmentId] = undefined;
      })
      .addCase(fetchSensorData.fulfilled, (state, action) => {
        const { projectId, assignmentId } = action.payload;
        if (!state.byProject[projectId]) return;
        state.byProject[projectId].loading.sensorData[assignmentId] = false;
        state.byProject[projectId].error.sensorData[assignmentId] = undefined;
      })
      .addCase(fetchSensorData.rejected, (state, action) => {
        const { projectId, assignmentId } = action.meta.arg;
        if (!state.byProject[projectId]) return;
        state.byProject[projectId].loading.sensorData[assignmentId] = false;
        state.byProject[projectId].error.sensorData[assignmentId] =
          action.payload || 'Unknown error';
      })
      .addCase(cancelSensorOrder.fulfilled, (state, action) => {
        const { projectId, orderId } = action.payload;
        if (!state.byProject[projectId]) return;
        state.byProject[projectId].sensorOrders = state.byProject[
          projectId
        ].sensorOrders.filter((o) => o.id !== orderId);
      })
      .addCase(fetchStrengthData.pending, (state, action) => {
        const { projectId, assignmentId } = action.meta.arg;
        ensureProject(state, projectId);
        state.byProject[projectId].loadingStarted.strength[assignmentId] = true;
      })
      .addCase(fetchCompanyDevices.pending, (state, action) => {
        const { projectId } = action.meta.arg;
        ensureProject(state, projectId);
        state.byProject[projectId].loading.companyDevices = true;
        state.byProject[projectId].loadingStarted.companyDevices = true;
        state.byProject[projectId].error.companyDevices = undefined;
      })
      .addCase(fetchCompanyDevices.fulfilled, (state, action) => {
        const { devices, projectId } = action.payload;
        ensureProject(state, projectId);
        state.byProject[projectId].companyDevices = devices;
        state.byProject[projectId].loading.companyDevices = false;
        state.byProject[projectId].error.companyDevices = undefined;
      })
      .addCase(fetchCompanyDevices.rejected, (state, action) => {
        const { projectId } = action.meta.arg;
        if (!state.byProject[projectId]) return;
        state.byProject[projectId].loading.companyDevices = false;
        state.byProject[projectId].error.companyDevices =
          action.payload || 'Unknown error';
      });
  },
});

export const { sensorOrderAdded, sensorOrderUpdated } = sensorDataSlice.actions;

export const selectDevicesForProject = (state, projectId) =>
  Object.values(state.renologSensorData.byProject[projectId]?.entities || {});

export const selectDeviceByAssignmentId = (state, projectId, assignmentId) =>
  state.renologSensorData.byProject[projectId]?.entities?.[assignmentId];

export const selectSensorReadings = (state, projectId, assignmentId) =>
  state.renologSensorData.byProject[projectId]?.sensorReadings?.[assignmentId];

export const selectDeviceLoading = (state, projectId) =>
  state.renologSensorData.byProject[projectId]?.loading?.devices ?? false;

export const selectDevicesLoadingStarted = (state, projectId) =>
  state.renologSensorData.byProject[projectId]?.loadingStarted?.devices ??
  false;

export const selectSensorDataLoading = (state, projectId, assignmentId) =>
  state.renologSensorData.byProject[projectId]?.loading?.sensorData?.[
    assignmentId
  ] ?? false;

export const selectTemperatureHumidityLoading = (
  state,
  projectId,
  assignmentId
) =>
  state.renologSensorData.byProject[projectId]?.loading?.temperatureHumidity?.[
    assignmentId
  ] ?? false;

export const selectMouldRiskLoading = (state, projectId, assignmentId) =>
  state.renologSensorData.byProject[projectId]?.loading?.mouldRisk?.[
    assignmentId
  ] ?? false;

export const selectStrengthLoading = (state, projectId, assignmentId) =>
  state.renologSensorData.byProject[projectId]?.loading?.strength?.[
    assignmentId
  ] ?? false;

export const selectStrengthLoadingStarted = (state, projectId, assignmentId) =>
  state.renologSensorData.byProject[projectId]?.loadingStarted?.strength?.[
    assignmentId
  ] ?? false;

export const selectSensorDataLoadingStarted = (
  state,
  projectId,
  assignmentId
) =>
  state.renologSensorData.byProject[projectId]?.loadingStarted?.sensorData?.[
    assignmentId
  ] ?? false;

export const selectSensorDataLoadingStartedForProject = (state, projectId) =>
  state.renologSensorData.byProject[projectId]?.loadingStarted?.sensorData ||
  {};

export const selectSensorOrders = (state, projectId) =>
  state.renologSensorData.byProject[projectId]?.sensorOrders || [];

export const selectSensorOrdersLoadingStarted = (state, projectId) =>
  state.renologSensorData.byProject[projectId]?.loadingStarted?.sensorOrders ??
  false;

export const selectCompanyDevices = (state, projectId) =>
  state.renologSensorData.byProject[projectId]?.companyDevices || [];

export const selectCompanyDevicesLoadingStarted = (state, projectId) =>
  state.renologSensorData.byProject[projectId]?.loadingStarted
    ?.companyDevices ?? false;

export const selectCompanyDevicesLoading = (state, projectId) =>
  state.renologSensorData.byProject[projectId]?.loading?.companyDevices ??
  false;

export const selectDeviceError = (state, projectId) =>
  state.renologSensorData.byProject[projectId]?.error?.devices;

export const selectSensorDataError = (state, projectId, assignmentId) =>
  state.renologSensorData.byProject[projectId]?.error?.sensorData?.[
    assignmentId
  ];

export default sensorDataSlice.reducer;
